import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Camera, Loader2, RotateCcw, X } from "lucide-react";

import { useToast } from "@/features/toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useScreenshotCaptureStore } from "../store/screenshot-capture-store";
import { SCREENSHOT_EXCLUDE_ATTR, captureViewport } from "../utils/screenshot";

interface Preview {
  file: File;
  url: string;
}

/**
 * Uygulama genelinde çalışan ekran görüntüsü yakalama modu.
 *
 * Destek talebinde bulunan kullanıcı çoğu zaman ticket ekranını değil,
 * sorunu yaşadığı başka bir sayfayı göstermek ister. Bu yüzden mod
 * açıldığında kullanıcı uygulamada serbestçe gezinir; buradaki sabit
 * çubuk ona eşlik eder ve "Yakala" dediği anda o sayfa görüntülenir.
 * Çubuğun kendisi görüntüye girmesin diye yakalama filtresiyle hariç
 * tutulur.
 */
export const ScreenshotCaptureOverlay: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { error: toastError } = useToast();

  const isCapturing = useScreenshotCaptureStore((state) => state.isCapturing);
  const returnTo = useScreenshotCaptureStore((state) => state.returnTo);
  const cancelCapture = useScreenshotCaptureStore(
    (state) => state.cancelCapture,
  );
  const finishCapture = useScreenshotCaptureStore(
    (state) => state.finishCapture,
  );

  const [isRendering, setIsRendering] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);

  const clearPreview = useCallback(() => {
    setPreview((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  }, []);

  const handleCapture = useCallback(async () => {
    setIsRendering(true);
    try {
      const file = await captureViewport();
      setPreview({ file, url: URL.createObjectURL(file) });
    } catch (error) {
      console.error("Screenshot capture failed:", error);
      toastError(t("support.tools.captureFailed"));
    } finally {
      setIsRendering(false);
    }
  }, [t, toastError]);

  const handleCancel = useCallback(() => {
    clearPreview();
    cancelCapture();
    if (returnTo) navigate(returnTo);
  }, [cancelCapture, clearPreview, navigate, returnTo]);

  const handleAttach = useCallback(() => {
    if (!preview) return;
    finishCapture(preview.file);
    clearPreview();
    if (returnTo) navigate(returnTo);
  }, [clearPreview, finishCapture, navigate, preview, returnTo]);

  // Esc ile moddan çıkış — önizleme açıkken diyalog kendi kapanışını yönetir
  useEffect(() => {
    if (!isCapturing || preview) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleCancel, isCapturing, preview]);

  useEffect(() => () => clearPreview(), [clearPreview]);

  if (!isCapturing) return null;

  return (
    <>
      {/* Header z-[950] ve dropdown'lar z-[999] kullanıyor; mod göstergesi
          onların da üstünde kalmalı, yalnızca toast'ların altında */}
      <div
        {...{ [SCREENSHOT_EXCLUDE_ATTR]: "" }}
        className="pointer-events-none fixed inset-0 z-[9990]"
      >
        {/* Modun aktif olduğunu belli eden ince çerçeve */}
        <div className="absolute inset-0 rounded-sm ring-2 ring-inset ring-indigo-500/60" />

        <div className="pointer-events-auto absolute bottom-6 left-1/2 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-full border border-border bg-card py-2 pl-4 pr-2 text-foreground shadow-lg">
          <span className="flex items-center gap-2 text-sm">
            <Camera className="h-4 w-4 shrink-0 text-indigo-500 dark:text-indigo-400" />
            <span className="hidden sm:inline">
              {t("support.tools.captureBarHint")}
            </span>
          </span>

          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleCancel}
              disabled={isRendering}
              className="rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="mr-1 h-3.5 w-3.5" />
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleCapture()}
              disabled={isRendering}
              className="rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 text-white hover:opacity-90 dark:from-indigo-500 dark:to-violet-500"
            >
              {isRendering ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Camera className="mr-1.5 h-3.5 w-3.5" />
              )}
              {t("support.tools.captureNow")}
            </Button>
          </div>
        </div>
      </div>

      <Dialog
        open={!!preview}
        onOpenChange={(open) => {
          if (!open) clearPreview();
        }}
      >
        <DialogContent
          open={!!preview}
          cleanupMode="scoped"
          className="flex max-h-[90dvh] w-full max-w-3xl flex-col gap-0 lg:max-w-5xl"
        >
          {/* Başlık — hafif muted yüzey + alt çizgi */}
          <div className="shrink-0 border-b border-border/40 bg-muted/[0.15] px-6 pb-4 pt-5">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg font-medium text-foreground">
                <Camera className="h-4 w-4 shrink-0 text-indigo-500 dark:text-indigo-400" />
                {t("support.tools.capturePreviewTitle")}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {t("support.tools.capturePreviewDescription")}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Görsel önizleme */}
          {preview && (
            <div className="min-h-0 flex-1 overflow-auto p-4">
              <div className="overflow-hidden rounded-xl border border-border/60 shadow-sm">
                <img
                  src={preview.url}
                  alt={t("support.tools.capturePreviewTitle")}
                  className="block w-full"
                />
              </div>
            </div>
          )}

          {/* Footer butonlar — hafif muted yüzey + üst çizgi */}
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border/40 bg-muted/[0.15] px-6 pb-5 pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              className="text-foreground hover:bg-muted"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={clearPreview}
              className="dark:border-border dark:bg-muted/50 dark:text-foreground dark:hover:bg-muted"
            >
              <RotateCcw className="mr-1.5 h-4 w-4" />
              {t("support.tools.captureRetake")}
            </Button>
            <Button
              type="button"
              onClick={handleAttach}
              className="bg-gradient-to-br from-indigo-600 to-violet-600 text-white hover:opacity-90 dark:from-indigo-500 dark:to-violet-500"
            >
              {t("support.tools.captureAttach")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
